import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { api as apiAlias } from '../api/alias'
import { type Alias } from '../api/types'

interface AliasEditProps {
  alias: Alias
  open: boolean
  onClose: () => void
  onSaved: (updated: Alias) => void
}

export const AliasEdit: React.FC<AliasEditProps> = ({
  alias,
  open,
  onClose,
  onSaved
}) => {
  const [description, setDescription] = useState(alias.description)
  const [expires, setExpires] = useState(alias.expires)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    setDescription(alias.description)
    setExpires(alias.expires)
  }, [alias])

  const handleSave = (): void => {
    setSaving(true)
    const updated: Alias = { ...alias, description, expires }
    void apiAlias
      .update({ group: updated.group, alias: updated.alias }, updated)
      .then(() => {
        onSaved(updated)
        onClose()
      })
      .catch((err: Error) => {
        setError(err.message)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Alias</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {alias.alias}
          </Typography>
          <TextField
            label="Description"
            placeholder="Optional note about this alias"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
            }}
            fullWidth
            multiline
            maxRows={3}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiration date (optional)"
              value={expires !== '' ? dayjs(expires) : null}
              onChange={(value: Dayjs | null) => {
                setExpires(value !== null ? value.format('YYYY-MM-DD') : '')
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={error !== undefined}
        autoHideDuration={4000}
        onClose={() => {
          setError(undefined)
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </>
  )
}
